import type { ResourcePackTextures } from '../render/structureAssets';
import { blockAppearance, type BlockAppearance } from './blockAppearance';
import { TEXTURE_SIZE, renderProceduralTexture } from './procedural';
import type { SourceTexture } from './atlas';

/** 一覧表示用に、テクスチャ1枚をdata URLへ焼く。 */
export function textureDataUrl(name: string, source?: SourceTexture): string | null {
  const pixels = source ?? { data: renderProceduralTexture(name), size: TEXTURE_SIZE };
  const canvas = document.createElement('canvas');
  canvas.width = pixels.size;
  canvas.height = pixels.size;
  const context = canvas.getContext('2d');
  if (!context) return null;
  const image = context.createImageData(pixels.size, pixels.size);
  image.data.set(pixels.data);
  context.putImageData(image, 0, 0);
  return canvas.toDataURL();
}

/** Small isometric model: stairs and slabs remain recognisable in material lists. */
export function blockDataUrl(name: string, sources?: Map<string, SourceTexture>, resolved?: BlockAppearance): string | null {
  if (resolved?.quads) return modelDataUrl(resolved, sources);
  const appearance = blockAppearance(name, { facing: 'south', east: 'true', west: 'true' });
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 64;
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.imageSmoothingEnabled = false;
  const point = (x: number, y: number, z: number) => [32 + (x - z) * 27, 4 + (x + z) * 14 + (1 - y) * 28];
  const tile = (face: number) => {
    const texture = appearance.faces[face];
    const source = sources?.get(texture) ?? { size: TEXTURE_SIZE, data: renderProceduralTexture(texture) };
    const image = document.createElement('canvas');
    image.width = image.height = source.size;
    const ctx = image.getContext('2d')!;
    const pixels = ctx.createImageData(source.size, source.size);
    pixels.data.set(source.data);
    ctx.putImageData(pixels, 0, 0);
    return image;
  };
  if (appearance.cross) {
    context.drawImage(tile(0), 8, 4, 48, 56);
    return canvas.toDataURL();
  }
  for (const box of [...appearance.boxes].sort((a, b) => a.from[0] + a.from[2] - b.from[0] - b.from[2])) {
    const [x0, y0, z0] = box.from;
    const [x1, y1, z1] = box.to;
    const faces = [
      { face: 0, origin: point(x1, y1, z1), right: point(x1, y1, z0), down: point(x1, y0, z1), crop: [1 - z1, 1 - y1, z1 - z0, y1 - y0], shade: 0.22 },
      { face: 4, origin: point(x0, y1, z1), right: point(x1, y1, z1), down: point(x0, y0, z1), crop: [x0, 1 - y1, x1 - x0, y1 - y0], shade: 0.1 },
      { face: 2, origin: point(x0, y1, z0), right: point(x1, y1, z0), down: point(x0, y1, z1), crop: [x0, z0, x1 - x0, z1 - z0], shade: 0 },
    ];
    for (const { face, origin, right, down, crop, shade } of faces) {
      const texture = tile(face);
      context.save();
      context.setTransform(right[0] - origin[0], right[1] - origin[1], down[0] - origin[0], down[1] - origin[1], origin[0], origin[1]);
      context.drawImage(texture, crop[0] * texture.width, crop[1] * texture.height, crop[2] * texture.width, crop[3] * texture.height, 0, 0, 1, 1);
      // Source-atop preserves transparent holes in glass and doors.
      context.globalCompositeOperation = 'source-atop';
      context.fillStyle = `rgba(0,0,0,${shade})`;
      context.fillRect(0, 0, 1, 1);
      context.restore();
    }
  }
  return canvas.toDataURL();
}

/** Project the same model faces used by the scene; crop/rotation/holes remain identical. */
function modelDataUrl(appearance: BlockAppearance, sources?: Map<string, SourceTexture>): string | null {
  const canvas = document.createElement('canvas'); canvas.width=canvas.height=64;
  const ctx=canvas.getContext('2d'); if(!ctx)return null;
  ctx.imageSmoothingEnabled=false;
  const quads=(appearance.quads??[]).filter(q=>{
    const p=q.corners, a=[p[3]-p[0],p[4]-p[1],p[5]-p[2]],b=[p[6]-p[0],p[7]-p[1],p[8]-p[2]];
    return a[1]*b[2]-a[2]*b[1]+a[2]*b[0]-a[0]*b[2]+a[0]*b[1]-a[1]*b[0]>0;
  }).sort((a,b)=>a.corners.reduce((s,v)=>s+v,0)-b.corners.reduce((s,v)=>s+v,0));
  const project=(x:number,y:number,z:number)=>[(x-z)*27,(x+z)*14-y*28];
  const all=quads.flatMap(q=>[0,3,6,9].map(i=>project(q.corners[i],q.corners[i+1],q.corners[i+2])));
  if(!all.length)return canvas.toDataURL();
  const minX=Math.min(...all.map(p=>p[0])),maxX=Math.max(...all.map(p=>p[0]));
  const minY=Math.min(...all.map(p=>p[1])),maxY=Math.max(...all.map(p=>p[1]));
  const scale=Math.min(56/(maxX-minX||1),56/(maxY-minY||1));
  for(const q of quads) {
    const source=sources?.get(q.texture); if(!source)continue;
    const image=document.createElement('canvas');image.width=image.height=source.size;
    const ic=image.getContext('2d')!,pixels=ic.createImageData(source.size,source.size);pixels.data.set(source.data);
    const tint=q.tint??0xffffff;
    for(let i=0;i<pixels.data.length;i+=4)for(let channel=0;channel<3;channel++)pixels.data[i+channel]*=q.shade*((tint>>(16-channel*8))&255)/255;
    ic.putImageData(pixels,0,0);
    const dest=[0,3,6,9].map(i=>{const p=project(q.corners[i],q.corners[i+1],q.corners[i+2]);return [32+(p[0]-(minX+maxX)/2)*scale,32+(p[1]-(minY+maxY)/2)*scale];});
    for(const indices of [[0,1,2],[0,2,3]]) {
      const [a,b,c]=indices.map(i=>[q.uv[i*2]*source.size,q.uv[i*2+1]*source.size]);
      const [A,B,C]=indices.map(i=>dest[i]);
      const det=(b[0]-a[0])*(c[1]-a[1])-(c[0]-a[0])*(b[1]-a[1]);if(Math.abs(det)<1e-8)continue;
      const m=(axis:number)=>[( (B[axis]-A[axis])*(c[1]-a[1])-(C[axis]-A[axis])*(b[1]-a[1]))/det,((C[axis]-A[axis])*(b[0]-a[0])-(B[axis]-A[axis])*(c[0]-a[0]))/det];
      const [xx,xy]=m(0),[yx,yy]=m(1);
      ctx.save();ctx.beginPath();ctx.moveTo(...A as [number,number]);ctx.lineTo(...B as [number,number]);ctx.lineTo(...C as [number,number]);ctx.closePath();ctx.clip();
      ctx.setTransform(xx,yx,xy,yy,A[0]-xx*a[0]-xy*a[1],A[1]-yx*a[0]-yy*a[1]);ctx.drawImage(image,0,0);ctx.restore();
    }
  }
  return canvas.toDataURL();
}

/** Material cards use the game's flat item sprite; blocks retain their model preview. */
export function materialDataUrl(name: string, pack?: ResourcePackTextures | null): string | null {
  if (!pack) return null;
  const icon = pack.itemIcons?.get(name);
  if (icon) {
    const source = pack.textures.get(icon);
    return source ? textureDataUrl(icon, source) : null;
  }
  const model = pack.previews?.get(name);
  return model ? blockDataUrl(name, pack.textures, model) : null;
}
